import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  addDoc,
} from "firebase/firestore";
import { db } from "./firebase";

type Seat = {
  id: string;
  number: number;
  status: "available" | "occupied";
};

type History = {
  seatNumber: number;
  action: string;
  timestamp: Date;
};

const AdminPage: React.FC = () => {
  const [seats, setSeats] = useState<Seat[]>([]);
  const [isLocked, setIsLocked] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<History[]>([]);
  const [scale, setScale] = useState(1);

  const baseWidth = 1320;
  const baseHeight = 620;

  useEffect(() => {
    fetchSeats();
    fetchIsLocked();
    handleResize();

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  const handleResize = () => {
    const sidePadding = 32;
    const verticalPadding = 220;

    const availableWidth = window.innerWidth - sidePadding;
    const availableHeight = window.innerHeight - verticalPadding;

    const widthScale = availableWidth / baseWidth;
    const heightScale = availableHeight / baseHeight;

    const newScale = Math.min(widthScale, heightScale, 1.6);

    setScale(newScale);
  };

  const fetchSeats = async () => {
    const snap = await getDocs(collection(db, "seats"));
    const data = snap.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as Omit<Seat, "id">),
    }));
    data.sort((a, b) => a.number - b.number);
    setSeats(data);
  };

  const fetchIsLocked = async () => {
    const configSnap = await getDoc(doc(db, "settings", "config"));
    if (configSnap.exists()) {
      setIsLocked(configSnap.data().isLocked);
    }
  };

  const toggleSeatStatus = async (seat: Seat) => {
    if (isLocked) return;

    const newStatus = seat.status === "available" ? "occupied" : "available";

    await updateDoc(doc(db, "seats", seat.id), {
      status: newStatus,
    });

    await addDoc(collection(db, "history"), {
      seatNumber: seat.number,
      action: newStatus === "available" ? "空席に変更" : "使用中に変更",
      timestamp: new Date(),
    });

    fetchSeats();
  };

  const toggleLock = async () => {
    const configRef = doc(db, "settings", "config");

    await updateDoc(configRef, {
      isLocked: !isLocked,
    });

    setIsLocked(!isLocked);
  };

  const fetchHistory = async () => {
    const snap = await getDocs(collection(db, "history"));

    const data = snap.docs.map((doc) => {
      const d = doc.data();
      return {
        seatNumber: Number(d.seatNumber),
        action: d.action,
        timestamp: d.timestamp.toDate(),
      };
    });

    data.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    setHistory(data);
  };

  const layoutMap: { [num: number]: { row: number; col: number } } = {
    1: { col: 7, row: 8 },
    2: { col: 5, row: 8 },
    3: { col: 3, row: 8 },
    4: { col: 1, row: 8 },

    5: { col: 1, row: 6 },
    6: { col: 1, row: 4 },
    7: { col: 1, row: 2 },

    8: { col: 3, row: 6 },
    9: { col: 3, row: 4 },
    10: { col: 3, row: 2 },

    11: { col: 5, row: 2 },
    12: { col: 5, row: 4 },
    13: { col: 5, row: 6 },

    14: { col: 8, row: 6 },
    15: { col: 8, row: 4 },
  };

  const getSeatWrapperStyle = (seatNumber: number): React.CSSProperties => {
    const base: React.CSSProperties = {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1,
    };

    switch (seatNumber) {
      case 8:
        return { ...base, transform: "translate(35px, 12px)" };
      case 9:
        return { ...base, transform: "translate(35px, 0px)" };
      case 10:
        return { ...base, transform: "translate(35px, -12px)" };

      case 11:
        return { ...base, transform: "translate(-35px, -12px)" };
      case 12:
        return { ...base, transform: "translate(-35px, 0px)" };
      case 13:
        return { ...base, transform: "translate(-35px, 12px)" };

      case 14:
      case 15:
        return { ...base, transform: "translateX(-60px)" };

      default:
        return base;
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: 16,
        boxSizing: "border-box",
        fontFamily: "sans-serif",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <h1 style={{ marginBottom: 16 }}>管理者ページ</h1>

      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <button
          onClick={toggleLock}
          style={{
            padding: "6px 12px",
            fontSize: "14px",
            backgroundColor: isLocked ? "#ccc" : "#007bff",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          {isLocked ? "操作ロック中（解除）" : "操作ロック中止（有効）"}
        </button>

        <button
          onClick={() => {
            fetchHistory();
            setShowHistory(!showHistory);
          }}
          style={{
            padding: "6px 12px",
            fontSize: "14px",
            backgroundColor: "#6c757d",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          {showHistory ? "履歴を隠す" : "履歴を表示"}
        </button>
      </div>

     <div
  style={{
    width: baseWidth * scale,
    height: baseHeight * scale,
    display: "flex",
    justifyContent: "center",
  }}
>
  <div
    style={{
      width: baseWidth,
      height: baseHeight,
      transform: `scale(${scale})`,
      transformOrigin: "top center",
    }}
  >
          <div
            style={{
              display: "grid",
              position: "relative",
              gridTemplateRows:
                "60px 120px 30px 120px 30px 120px 60px 120px",
              gridTemplateColumns:
                "120px 60px 120px 60px 120px 60px 120px 120px 120px 120px",
              gap: "6px",
            }}
          >
            <div
              style={{
                gridColumn: "3 / 6",
                gridRow: "2 / 7",
                marginTop: -25,
                marginBottom: -25,
                border: "4px solid #555",
                borderRadius: 20,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />

            <div
              style={{
                gridColumn: "9 / 11",
                gridRow: "2 / 7",
                border: "4px dashed #888",
                borderRadius: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                fontSize: 18,
                zIndex: 0,
                pointerEvents: "none",
              }}
            >
              授業スペース
            </div>

            {seats
              .filter((seat) => seat.number >= 1 && seat.number <= 15)
              .map((seat) => {
                const pos = layoutMap[seat.number];
                if (!pos) return null;

                return (
                  <div
                    key={seat.id}
                    style={{
                      gridRow: pos.row,
                      gridColumn: pos.col,
                      ...getSeatWrapperStyle(seat.number),
                    }}
                  >
                    <button
                      onClick={() => toggleSeatStatus(seat)}
                      disabled={isLocked}
                      style={{
                        width: 100,
                        height: 100,
                        backgroundColor:
                          seat.status === "available" ? "#90EE90" : "#FF7F7F",
                        border: "4px solid black",
                        borderRadius: 18,
                        boxShadow: "4px 4px 10px rgba(0,0,0,0.3)",
                        fontSize: 22,
                        color: "white",
                        cursor: isLocked ? "not-allowed" : "pointer",
                      }}
                    >
                      {seat.number}
                    </button>

                    <div style={{ marginTop: 4, fontWeight: "bold" }}>
                      {seat.status === "available" ? "空席" : "使用中"}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {showHistory && (
        <div
          style={{
            marginTop: 30,
            width: "100%",
            maxWidth: 900,
            maxHeight: 300,
            overflowY: "scroll",
            padding: 10,
            border: "1px solid #ccc",
            borderRadius: 6,
            backgroundColor: "#f9f9f9",
            boxSizing: "border-box",
          }}
        >
          {history.map((h, i) => (
            <div key={i} style={{ marginBottom: 8, fontSize: "14px" }}>
              {`${h.seatNumber}番: ${h.action}（${h.timestamp.toLocaleString()}）`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPage;